import React, {
  useMemo, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Row,
  Divider,
  message,
} from 'antd';

import CompanySettingsCard from '../CompanySettingsCard';

import AutomaticOvertime from './AutomaticOvertime';
import ManualOvertime from './ManualOvertime';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import OvertimeSettingsStatus from './OvertimeSettingsStatus';

import colors from '../../constants/Colors';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';
import {
  confirmOvertimeSettingsSave,
  getLatestSavedOvertimeSettings,
  getOvertimeSettingsDiff,
  hasOvertimeSettingsChanges,
} from '../../helpers/overtime/overtimeSettingsHelpers';

export default function OvertimeContent({
  settings: settingsProp,
  onSettingsChange,
  saveCompanySettings,
  deferSave = false,
  showCard = true,
  otProfileId = null,
}) {
  const [saving, setSaving] = useState(false);

  const history = useSelector((state) => state.settings.otSettingsVersions);

  const versionsKey = otProfileId || 'company';
  const versionHistoryEntry = history?.[versionsKey];
  const versionHistory = versionHistoryEntry?.versions ?? [];

  // GETs return currently-applied OT; form edits latest when a version is scheduled.
  const settings = useMemo(() => (
    deferSave
      ? settingsProp
      : getLatestSavedOvertimeSettings(settingsProp, versionHistoryEntry || {})
  ), [deferSave, settingsProp, versionHistoryEntry]);

  const [draftSettings, setDraftSettings] = useState(settings);

  useEffect(() => {
    if (!deferSave) {
      setDraftSettings(settings);
    }
  }, [settings, deferSave]);

  const displaySettings = deferSave ? settings : draftSettings;
  const { enableManualOT } = displaySettings;

  const handleSettingsChange = useCallback((updates) => {
    if (deferSave) {
      onSettingsChange(updates);
      return;
    }
    setDraftSettings((prev) => ({ ...prev, ...updates }));
  }, [deferSave, onSettingsChange]);

  const hasChanges = !deferSave && hasOvertimeSettingsChanges(settings, draftSettings);

  const performSave = useCallback(async () => {
    const diff = getOvertimeSettingsDiff(settings, draftSettings);
    if (!Object.keys(diff).length) return;

    setSaving(true);
    try {
      await saveCompanySettings(diff);
      message.success('Overtime settings saved');
    } catch {
      message.error('Failed to save overtime settings');
    }
    setSaving(false);
  }, [settings, draftSettings, saveCompanySettings]);

  const handleSave = useCallback(() => {
    if (!hasChanges) return;
    confirmOvertimeSettingsSave({
      companySettings: settings,
      savedSettings: settings,
      draftSettings,
      versionHistory,
      onConfirm: performSave,
    });
  }, [hasChanges, settings, draftSettings, versionHistory, performSave]);

  const onSwitch = () => {
    handleSettingsChange({ enableManualOT: !enableManualOT });
  };

  const body = useMemo(() => (
    enableManualOT
      ? (
        <ManualOvertime
          settings={displaySettings}
          onSettingsChange={handleSettingsChange}
        />
      )
      : (
        <AutomaticOvertime
          settings={displaySettings}
          onSettingsChange={handleSettingsChange}
          immediateToggleCommit={deferSave}
        />
      )
  ), [enableManualOT, displaySettings, handleSettingsChange, deferSave]);

  const content = (
    <>
      <OvertimeSettingsStatus otProfileId={otProfileId} />
      <Row style={{ margin: '0 32px 20px 0px' }} justify="space-between" align="middle">
        <OnTraccrSwitch
          value={enableManualOT}
          onChange={onSwitch}
          title={enableManualOT ? 'Manual Overtime' : 'Automatic Overtime'}
        />
        {!deferSave && (
          <OnTraccrButton
            title="Save"
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges}
          />
        )}
      </Row>
      <Divider style={{
        backgroundColor: colors.ONTRACCR_OPACITY_GRAY,
        maxWidth: 440,
        minWidth: 440,
      }}
      />
      {body}
    </>
  );

  if (showCard) {
    return (
      <CompanySettingsCard title="Overtime">
        {content}
      </CompanySettingsCard>
    );
  }
  return content;
}

OvertimeContent.propTypes = {
  settings: PropTypes.shape({
    enableManualOT: PropTypes.bool,
  }).isRequired,
  onSettingsChange: PropTypes.func,
  saveCompanySettings: PropTypes.func,
  deferSave: PropTypes.bool,
  showCard: PropTypes.bool,
  otProfileId: PropTypes.string,
};

OvertimeContent.defaultProps = {
  onSettingsChange: () => {},
  saveCompanySettings: () => {},
  deferSave: false,
  showCard: true,
  otProfileId: null,
};
